import { useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { createLogger } from '@/utils/logger'

const logger = createLogger('BookmarkDetail')
import { ArrowLeft, BookOpen, Database, ExternalLink, FileText, Loader2, RefreshCw, Sparkles } from 'lucide-react'
import { useBookmark, useEnrichBookmark } from '@/hooks/useBookmarks'
import { MarkdownContent } from '@/components/chat/MarkdownContent'

import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type { BookmarkResponse, PubMedReference, SAIReference, CitationResponse } from '@/api/types'
import { TRANSIENT_FAILURE_REASONS } from '@/api/types'
import { POLL_INTERVAL_BOOKMARK_ENRICHMENT } from '@/lib/constants'
import { navigateToSession } from '@/lib/sessionRouting'

/**
 * Returns true when a failed bookmark is expected to be retried automatically.
 * Criteria: failure_reason is transient AND attempt_count < max_attempts.
 *
 * TODO: Currently always returns false because backend sets max_attempts=1.
 * Will become reachable when backend implements auto-retry (max_attempts > 1).
 */
function isAwaitingRetry(bookmark: BookmarkResponse): boolean {
  if (bookmark.enrichment_status !== 'failed') return false
  const meta = bookmark.enrichment_metadata
  if (!meta) return false
  if (!meta.failure_reason) return false
  return (
    TRANSIENT_FAILURE_REASONS.has(meta.failure_reason) &&
    meta.attempt_count < meta.max_attempts
  )
}

export default function BookmarkDetail() {
  const { t } = useTranslation()
  const { bookmarkId } = useParams<{ bookmarkId: string }>()
  const navigate = useNavigate()
  const { data: bookmark, isLoading, refetch } = useBookmark(bookmarkId ?? null)
  const enrichMutation = useEnrichBookmark()

  // Track whether we've already attempted enrichment for this bookmark.
  // Prevents infinite loop: mutation fails → isPending flips → useEffect re-fires.
  const enrichAttemptedRef = useRef<string | null>(null)

  // Auto-trigger enrichment once when viewing a pending bookmark
  useEffect(() => {
    if (
      bookmark &&
      bookmark.enrichment_status === 'pending' &&
      !enrichMutation.isPending &&
      enrichAttemptedRef.current !== bookmark.id
    ) {
      enrichAttemptedRef.current = bookmark.id
      enrichMutation.mutate(bookmark.id, {
        onSuccess: () => {
          void refetch()
        },
      })
    }
    // Keyed on the bookmark's id + status ONLY. `enrichMutation` is a react-query
    // mutation whose identity changes on every status transition, so depending on
    // it re-enters this effect on its own side effect — the exact enrich loop the
    // ref above exists to stop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookmark?.id, bookmark?.enrichment_status])

  // Poll for enrichment completion while status is "processing"
  useEffect(() => {
    if (bookmark?.enrichment_status === 'processing') {
      const interval = setInterval(() => {
        logger.debug('[BookmarkDetail] Polling for enrichment completion...')
        void refetch()
      }, POLL_INTERVAL_BOOKMARK_ENRICHMENT)
      return () => clearInterval(interval)
    }
  }, [bookmark?.enrichment_status, refetch])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 p-4">
        <div className="space-y-4">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    )
  }

  if (!bookmark) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-sm text-neutral-600 mb-2">{t('bookmarks.notFound', 'Bookmark not found')}</p>
          <button
            onClick={() => navigate('/home')}
            className="text-sm text-primary font-medium"
          >
            {t('common.backHome', 'Back to Home')}
          </button>
        </div>
      </div>
    )
  }

  const isEnriching = bookmark.enrichment_status === 'pending' || bookmark.enrichment_status === 'processing' || enrichMutation.isPending
  const isEnriched = bookmark.enrichment_status === 'completed'
  const isFailed = bookmark.enrichment_status === 'failed'
  const isRetrying = isFailed && isAwaitingRetry(bookmark)
  const isPermanentlyFailed = isFailed && !isRetrying
  const hasPubMed = bookmark.pubmed_references && bookmark.pubmed_references.length > 0
  const hasSAI = bookmark.sai_references && bookmark.sai_references.length > 0
  const hasStudyNotes = !!bookmark.study_notes

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-neutral-50/80 backdrop-blur-xl border-b border-neutral-200/50">
        <div className="px-4 h-14 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-lg hover:bg-neutral-100"
            aria-label={t('common.goBack', 'Go back')}
          >
            <ArrowLeft className="w-5 h-5 text-neutral-600" />
          </button>
          <h1 className="text-lg font-display font-semibold text-neutral-900 flex-1">
            {t('bookmark.studyTitle', 'Study Bookmark')}
          </h1>
          <button
            onClick={() => navigateToSession({ id: bookmark.session_id, agent: { slug: bookmark.agent_slug, version: '' } }, navigate)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            <span>{t('bookmark.viewChat', 'View chat')}</span>
          </button>
        </div>
      </header>

      <main className="p-4 pb-24 space-y-6">
        {/* Enrichment Status - Processing / Pending */}
        {isEnriching && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
            <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
            <div>
              <p className="text-sm font-medium text-amber-800">{t('bookmark.enrichingDetail', 'Enriching bookmark...')}</p>
              <p className="text-xs text-amber-600">{t('bookmark.enrichingSubtitle', 'Searching PubMed and medical databases')}</p>
            </div>
          </div>
        )}

        {/* Enrichment Status - Awaiting automatic retry (transient failure) */}
        {isRetrying && !enrichMutation.isPending && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
            <RefreshCw className="w-5 h-5 text-amber-600 animate-spin" />
            <div>
              <p className="text-sm font-medium text-amber-800">{t('bookmark.enrichmentRetrying', 'Retrying...')}</p>
              <p className="text-xs text-amber-600">{t('bookmark.enrichmentRetryingDetail', 'Retrying literature search')}</p>
            </div>
          </div>
        )}

        {/* Enrichment Status - Permanent failure (exhausted retries or non-transient error) */}
        {isPermanentlyFailed && !enrichMutation.isPending && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 border border-red-200">
            <Sparkles className="w-5 h-5 text-red-600" />
            <div>
              <p className="text-sm font-medium text-red-800">{t('bookmark.enrichmentFailed', 'Enrichment failed')}</p>
              <p className="text-xs text-red-600">{t('bookmark.enrichmentRetryHint', 'Click retry to try again')}</p>
            </div>
            <button
              onClick={() => enrichMutation.mutate(bookmark.id, { onSuccess: () => { void refetch() } })}
              className="ml-auto px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200"
            >
              {t('actions.retry')}
            </button>
          </div>
        )}

        {/* Original Content */}
        <section className="bg-white rounded-xl border border-neutral-200/50 p-4">
          <h2 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-3">
            {t('bookmark.savedContent', 'Saved Content')}
          </h2>
          {bookmark.content_summary && (
            <div className="mb-3">
              <MarkdownContent content={bookmark.content_summary} isSummary />
            </div>
          )}
          {bookmark.content_details && (
            <div className="text-sm text-neutral-700">
              <MarkdownContent content={bookmark.content_details} />
            </div>
          )}
        </section>

        {/* Original Citations */}
        {bookmark.citations && bookmark.citations.length > 0 && (
          <section className="bg-white rounded-xl border border-neutral-200/50 p-4">
            <h2 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {t('bookmark.originalSources', 'Original Sources')} ({bookmark.citations.length})
            </h2>
            <div className="space-y-2">
              {bookmark.citations.map((citation: CitationResponse, i: number) => (
                <CitationCard key={i} citation={citation} />
              ))}
            </div>
          </section>
        )}

        {/* Study Notes */}
        {isEnriched && hasStudyNotes && (
          <section className="bg-primary-50 rounded-xl border border-primary/20 p-4">
            <h2 className="text-sm font-semibold text-primary uppercase tracking-wide mb-3 flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              {t('bookmark.aiStudySummary', 'AI Study Summary')}
            </h2>
            <div className="text-sm text-neutral-800">
              <MarkdownContent content={bookmark.study_notes!} />
            </div>
          </section>
        )}

        {/* PubMed References */}
        {isEnriched && hasPubMed && (
          <section className="bg-white rounded-xl border border-neutral-200/50 p-4">
            <h2 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-3 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-blue-600" />
              {t('bookmark.pubmedLiterature', 'PubMed Literature')} ({bookmark.pubmed_references!.length})
            </h2>
            <div className="space-y-3">
              {bookmark.pubmed_references!.map((ref: PubMedReference) => (
                <PubMedCard key={ref.pmid} reference={ref} />
              ))}
            </div>
          </section>
        )}

        {/* SAI Medication References */}
        {isEnriched && hasSAI && (
          <section className="bg-white rounded-xl border border-neutral-200/50 p-4">
            <h2 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Database className="w-4 h-4 text-primary-600" />
              {t('bookmark.medicationInfo', 'Medication Information')} ({bookmark.sai_references!.length})
            </h2>
            <div className="space-y-3">
              {bookmark.sai_references!.map((ref: SAIReference, i: number) => (
                <SAICard key={i} reference={ref} />
              ))}
            </div>
          </section>
        )}

        {/* No enrichment data */}
        {isEnriched && !hasPubMed && !hasSAI && !hasStudyNotes && (
          <div className="text-center py-8 text-neutral-500">
            <p className="text-sm">{t('bookmark.noReferences', 'No additional references found for this topic.')}</p>
          </div>
        )}
      </main>

    </div>
  )
}

// Citation card component
function CitationCard({ citation }: { citation: CitationResponse }) {
  const { t } = useTranslation()
  return (
    <a
      href={citation.url || '#'}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "block p-3 rounded-lg",
        "bg-citation-50 border border-citation-200",
        "hover:bg-citation-100 transition-colors",
        !citation.url && "pointer-events-none"
      )}
    >
      <p className="text-sm font-medium text-citation-800">{citation.document}</p>
      {citation.page_start && (
        <p className="text-xs text-citation-600 mt-1">
          {t('bookmark.pages', 'Pages')} {citation.page_start}
          {citation.page_end && citation.page_end !== citation.page_start && `-${citation.page_end}`}
        </p>
      )}
      {citation.cited_text && (
        <p className="text-xs text-citation-700 mt-2 italic line-clamp-2">
          "{citation.cited_text}"
        </p>
      )}
    </a>
  )
}

// PubMed reference card
function PubMedCard({ reference }: { reference: PubMedReference }) {
  return (
    <a
      href={reference.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block p-3 rounded-lg bg-blue-50/50 border border-blue-100 hover:bg-blue-50 transition-colors"
    >
      <p className="text-sm font-medium text-blue-900">{reference.title}</p>
      <div className="flex items-center gap-2 mt-1 text-xs text-blue-700">
        {reference.authors && <span>{reference.authors}</span>}
        {reference.year && <span>({reference.year})</span>}
      </div>
      {reference.abstract && (
        <p className="text-xs text-blue-800 mt-2 line-clamp-3">{reference.abstract}</p>
      )}
      <p className="text-xs text-blue-600 mt-2 font-mono">PMID: {reference.pmid}</p>
    </a>
  )
}

// SAI medication card
function SAICard({ reference }: { reference: SAIReference }) {
  const { t } = useTranslation()
  return (
    <div className="p-3 rounded-lg bg-primary-50/50 border border-primary-100">
      <p className="text-sm font-medium text-primary-900">{reference.name}</p>
      {reference.section && (
        <p className="text-xs text-primary-700 mt-1 font-medium">{reference.section}</p>
      )}
      {reference.content_preview && (
        <p className="text-xs text-primary-800 mt-1 line-clamp-4">{reference.content_preview}</p>
      )}
      {reference.score != null && (
        <p className="text-xs text-primary-500 mt-2">
          {t('bookmark.relevance', 'Relevance')}: {Math.round(reference.score * 100)}%
        </p>
      )}
    </div>
  )
}
